import type { AiModelEnum, Annotation, EvidenceSessionAIModel } from 'api';
import { CenteredLoader, Stack, styled, TypographyIcon, useBreakpoint } from 'ui';
import { InfoOutlined } from 'ui/icons';

import { useSegmentationOverlay } from '../../hooks/useSegmentationOverlay/useSegmentationOverlay';
import { ZoomOverlay } from './ZoomOverlay';

interface CocoSegmentationOverlayProps {
  imageSrc: string;
  annotations: Annotation[];
  hiddenCategories?: string[];
  showLoader?: boolean;
  model: AiModelEnum | EvidenceSessionAIModel;
  showLegend?: boolean;
}

const Container = styled('div')<{ isMobile: boolean }>(({ isMobile }) => ({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'center',
  position: 'relative',
}));

const LoaderContainer = styled('div')({
  alignItems: 'center',
  display: 'flex',
  height: '100%',
  justifyContent: 'center',
  left: 0,
  position: 'absolute',
  top: 0,
  width: '100%',
});

const Canvas = styled('canvas')<{ isLoading: boolean }>(({ isLoading }) => ({
  height: 'auto',
  maxHeight: 'inherit',
  maxWidth: '100%',
  opacity: isLoading ? 0.3 : 1,
  transition: 'opacity 0.3s ease-in-out',
}));

export const SegmentationOverlay = ({
  imageSrc,
  annotations,
  hiddenCategories = [],
  showLoader = true,
  model,
  showLegend = true,
}: CocoSegmentationOverlayProps) => {
  const { isMobile } = useBreakpoint();

  const { canvasRect, canvasRef, isLoading, isZoomVisible, mousePosition, visibleAnnotations, labelColourMap } =
    useSegmentationOverlay({
      annotations,
      hiddenCategories,
      imageSrc,
      isMobile,
      model,
      showLegend,
    });

  const helpText = isMobile ? 'Zoom in with fingers' : 'Hover over photo to zoom';

  return (
    <Container isMobile={isMobile}>
      {showLoader && isLoading ? (
        <LoaderContainer>
          <CenteredLoader />
        </LoaderContainer>
      ) : null}
      <Stack spacing={1}>
        <TypographyIcon icon={InfoOutlined} size="small" color="text.secondary" fontSize="small">
          {helpText}
        </TypographyIcon>
        <Canvas
          ref={canvasRef}
          isLoading={isLoading}
          aria-label="Image with segmentation overlay"
          data-testid="segmentation-overlay"
        />
      </Stack>
      {isZoomVisible && !isMobile ? (
        <ZoomOverlay
          isVisible={isZoomVisible}
          mousePosition={mousePosition}
          sourceCanvas={canvasRef.current}
          sourceRect={canvasRect}
          imageSrc={imageSrc}
          annotations={visibleAnnotations}
          hiddenCategories={hiddenCategories}
          labelColourMap={labelColourMap}
        />
      ) : null}
    </Container>
  );
};
